import { useMemo, useState } from 'react';

import {
    IFeature,
    IPortfolioItemReportSearchFilterValuesFragment,
} from 'graphql/types';
import { getReportFilters } from 'report/constants/filter';
import { TReportType } from 'report/types';
import {
    coursesFilter,
    offerEventsFilter,
    trainingsFilter,
    scormsFilter,
    externalContentsFilter,
} from 'common/utils/drawerFilters';
import { IFilterFormItem } from 'common/types';
import { useFeature } from 'common/hooks/useFeature';

/*
 * This hook is used to manage the aggregated query filter values
 */
export const useReportQueryFilterValues = (type: TReportType) => {
    const [queryFilterValues, setQueryFilterValues] =
        useState<IPortfolioItemReportSearchFilterValuesFragment>();
    const { canUse: canUseScorm } = useFeature(IFeature.Scorm);
    const { canUse: canUseOffers } = useFeature(IFeature.Offer);
    const baseFilters = [...getReportFilters(type)];

    const filters = useMemo(() => {
        let trainingFilters;
        let offerEventFilters;

        switch (type) {
            case 'training':
                trainingFilters = queryFilterValues?.trainings || undefined;

                return [
                    ...baseFilters,
                    trainingsFilter(undefined, undefined, {
                        ids: trainingFilters?.map((training) => training.id),
                        isFilter: true,
                    }) as IFilterFormItem,
                ];

            case 'offer':
                offerEventFilters = queryFilterValues?.offerEvents || undefined;

                return [
                    ...baseFilters,
                    offerEventsFilter(undefined, undefined, {
                        ids: offerEventFilters?.map(
                            (offerEvent) => offerEvent.id
                        ),
                        isFilter: true,
                    }) as IFilterFormItem,
                ];
            case 'portfolio':
                trainingFilters =
                    queryFilterValues?.parentsTrainings || undefined;
                offerEventFilters =
                    queryFilterValues?.parentsOfferEvents || undefined;

                const portfolioReportFilters = [
                    ...baseFilters,
                    trainingsFilter(undefined, undefined, {
                        ids: trainingFilters?.map((training) => training.id),
                        isFilter: true,
                    }) as IFilterFormItem,
                ];

                if (canUseOffers) {
                    portfolioReportFilters.push(
                        offerEventsFilter(undefined, undefined, {
                            ids: offerEventFilters?.map(
                                (offerEvent) => offerEvent.id
                            ),
                            isFilter: true,
                        }) as IFilterFormItem
                    );
                }

                return portfolioReportFilters;
            case 'course':
                trainingFilters =
                    queryFilterValues?.parentsTrainings || undefined;
                offerEventFilters =
                    queryFilterValues?.parentsOfferEvents || undefined;
                const courseFilters = queryFilterValues?.courses || undefined;
                const scormFilters = queryFilterValues?.scorms || undefined;
                const externalContentFilters =
                    queryFilterValues?.externalContents || undefined;

                const courseReportFilters = [
                    ...baseFilters,
                    trainingsFilter(undefined, undefined, {
                        ids: trainingFilters?.map((training) => training.id),
                        isFilter: true,
                    }) as IFilterFormItem,
                ];

                if (canUseOffers) {
                    courseReportFilters.push(
                        offerEventsFilter(undefined, undefined, {
                            ids: offerEventFilters?.map(
                                (offerEvent) => offerEvent.id
                            ),
                            isFilter: true,
                        }) as IFilterFormItem
                    );
                }

                courseReportFilters.push(
                    coursesFilter(undefined, undefined, {
                        ids: courseFilters?.map((course) => course.id),
                        isFilter: true,
                    }) as IFilterFormItem
                );

                if (canUseScorm) {
                    courseReportFilters.push(
                        scormsFilter(undefined, undefined, {
                            ids: scormFilters?.map((scorm) => scorm.id),
                            isFilter: true,
                        }) as IFilterFormItem
                    );
                }

                return [
                    ...courseReportFilters,
                    externalContentsFilter(undefined, undefined, {
                        ids: externalContentFilters?.map(
                            (externalContent) => externalContent.id
                        ),
                        isFilter: true,
                    }) as IFilterFormItem,
                ];
            default:
                return baseFilters;
        }
    }, [type, queryFilterValues, baseFilters]);

    return {
        filters,
        queryFilterValues,
        setQueryFilterValues,
    };
};
